<template>
    <div :class="[backgroundClass, borderTopClass, borderBottomClass]">
        <div
            class="flex flex-row items-center gap-4 py-px justify-between pl-2 pr-1 shrink-0 text-[0.8125rem]/none"
            :class="[borderRightClass]"
            :style="minHeightStyle"
            @mouseover="hoverTimelinePart(props.rowId, null, false)"
        >
            <div class="flex flex-row text-gray-700 items-center font-light gap-2">
                <Tooltip>
                    <template #trigger="tooltipProps">
                        <TourIcon
                            class="size-4.5 shrink-0 rounded-xs cursor-help focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        />
                    </template>

                    <template #text>
                        <div>{{ $t('reference-allocations') }}</div>
                    </template>
                </Tooltip>

                <div class="flex flex-row items-center gap-1 min-w-max">
                    <Tooltip :is-disabled="props.allocationCount.total === 0">
                        <template #trigger="tooltipProps">
                            <span
                                class="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs"
                                :class="{ 'font-semibold': props.allocationCount.total > 0 }"
                                :aria-describedby="
                                    props.allocationCount.total === 0 ? undefined : tooltipProps['aria-labelledby']
                                "
                                :popovertarget="
                                    props.allocationCount.total === 0 ? undefined : tooltipProps.popovertarget
                                "
                                :tabindex="props.allocationCount.total === 0 ? undefined : 0"
                            >
                                {{ props.label }}
                            </span>

                            <span
                                v-if="props.allocationCount.total > 0"
                                class="font-light tabular-nums text-gray-500 ml-[0.5ch]"
                            >
                                <template v-if="props.allocationCount.inRange === props.allocationCount.total">
                                    ({{ props.allocationCount.total }})
                                </template>

                                <template v-if="props.allocationCount.inRange !== props.allocationCount.total">
                                    ({{ props.allocationCount.inRange }}/{{ props.allocationCount.total }})
                                </template>
                            </span>
                        </template>

                        <template #text>
                            <i18n
                                v-if="props.allocationCount.inRange === props.allocationCount.total"
                                path="location-contains-all-allocations"
                                :args="{ count: props.allocationCount.total }"
                            >
                                <template #allocationCount>
                                    <span class="font-semibold">
                                        {{ props.allocationCount.total }}
                                    </span>
                                </template>
                            </i18n>

                            <i18n
                                v-if="props.allocationCount.inRange !== props.allocationCount.total"
                                path="location-contains-allocations"
                                :args="{ count: props.allocationCount.total }"
                            >
                                <template #inRangeAllocationCount>
                                    <span class="font-semibold">
                                        {{ props.allocationCount.inRange }}
                                    </span>
                                </template>

                                <template #totalAllocationCount>
                                    <span class="font-semibold">
                                        {{ props.allocationCount.total }}
                                    </span>
                                </template>
                            </i18n>
                        </template>
                    </Tooltip>

                    <HelpButton v-if="props.tooltip" :text="props.tooltip" size="18px" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import HelpButton from '../../HelpButton/HelpButton.vue';
import TourIcon from '../../Icon/TourIcon.vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { useHeightSync } from '../composables/useHeightSync.ts';
import { hoveredTimelinePartsKey, hoverTimelinePartKey } from '../injectionKeys';

type ReferenceAllocationFirstColumnCellProps = {
    rowId: string;
    allocationCount: {
        inRange: number;
        total: number;
    };
    label: string;
    tooltip?: string;
};

const props = defineProps<ReferenceAllocationFirstColumnCellProps>();

const hoveredTimelineParts = inject(hoveredTimelinePartsKey);
if (!hoveredTimelineParts) {
    throw new Error('ReferenceAllocationFirstColumnCell must be used within a Timeline component');
}

const hoverTimelinePart = inject(hoverTimelinePartKey);
if (!hoverTimelinePart) {
    throw new Error('ReferenceAllocationFirstColumnCell must be used within a Timeline component');
}

const isRowHovered = computed(() => {
    return hoveredTimelineParts.rowId.value === props.rowId;
});

const backgroundClass = computed(() => {
    if (isRowHovered.value) {
        if (hoveredTimelineParts.isConflictCell.value) {
            return 'bg-red-100';
        }

        return 'bg-blue-100';
    }

    return 'bg-gray-50';
});

const borderTopClass = computed(() => {
    if (isRowHovered.value) {
        if (hoveredTimelineParts.isConflictCell.value) {
            return 'first-column-cell-border-top first-column-cell-border-top-1 first-column-cell-border-top-red-200';
        }

        return 'first-column-cell-border-top first-column-cell-border-top-1 first-column-cell-border-top-blue-200';
    }
});

const borderBottomClass = computed(() => {
    if (isRowHovered.value) {
        if (hoveredTimelineParts.isConflictCell.value) {
            return 'first-column-cell-border-bottom first-column-cell-border-bottom-1 first-column-cell-border-bottom-red-200';
        }

        return 'first-column-cell-border-bottom first-column-cell-border-bottom-1 first-column-cell-border-bottom-blue-200';
    }

    return 'first-column-cell-border-bottom first-column-cell-border-bottom-1 first-column-cell-border-bottom-gray-200';
});

const borderRightClass = computed(() => {
    if (isRowHovered.value) {
        return 'first-column-cell-border-right first-column-cell-border-right-2-max-height first-column-cell-border-right-blue-400';
    }
});

const { heightInRem } = useHeightSync(props.rowId);
const minHeightStyle = computed(() => {
    return {
        minHeight: `${heightInRem.value}rem`,
    };
});
</script>

<style scoped>
/**
 * These are needed to avoid ugly triangular intersections of 1px and 2px wide
 * borders which can happen due to border-collapse: separate; being used.
 * border-collapse: separate; is required to implement sticky columns/rows
 * without losing the border when cells are scrolled under the sticky ones.
 */

.first-column-cell-border-top,
.first-column-cell-border-bottom,
.first-column-cell-border-right {
    position: relative;
}

.first-column-cell-border-top-1::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 1px;
    z-index: 10;
}
.first-column-cell-border-top-red-200::before {
    background-color: var(--color-red-200);
}
.first-column-cell-border-top-blue-200::before {
    background-color: var(--color-blue-200);
}

.first-column-cell-border-bottom-1::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}
.first-column-cell-border-bottom-red-200::after {
    background-color: var(--color-red-200);
}
.first-column-cell-border-bottom-blue-200::after {
    background-color: var(--color-blue-200);
}
.first-column-cell-border-bottom-gray-200::after {
    background-color: var(--color-gray-200);
}

.first-column-cell-border-right-2::after {
    content: '';
    position: absolute;
    top: 0;
    right: -2px;
    width: 2px;
    height: calc(100%);
}
.first-column-cell-border-right-2-max-height::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -2px;
    width: 2px;
    height: calc(100% + 2px);
}
.first-column-cell-border-right-blue-400::after {
    background-color: var(--color-blue-400);
}
</style>
